import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../../firebase/AuthContext";
import { getApiUrl } from "../../../config/api";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import {
  t as trendT,
  bi,
  formatDate,
  Section,
  AllNews,
  StatPill,
  ArticlePreviewProvider,
} from "../TrendReports/shared/trendReportShared";

// ── TrendDashboardPage ──────────────────────────────────────────────────────
// Shown to users who are neither admin nor editor (PM). They only get the
// daily trend report — no product metrics, since those need PIMS access.
// Unlike the admin/PM dashboards, the report is rendered fully expanded
// (no collapse toggle) since it's the only content on the page.
function TrendDashboardPage() {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const getLangFromUrl = useCallback(() => {
    const params = new URLSearchParams(location.search);
    return params.get("lang") === "zh" ? "zh" : "en";
  }, [location.search]);

  const [language, setLanguage] = useState(getLangFromUrl);
  const [state, setState] = useState({ status: "loading", report: null });

  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  useEffect(() => {
    setLanguage(getLangFromUrl());
  }, [getLangFromUrl]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("lang") !== language) {
      params.set("lang", language);
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [language, location.search, navigate]);

  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;
    setState((s) => ({ status: "loading", report: s.report }));
    (async () => {
      try {
        const token = await currentUser.getIdToken();
        const res = await axios.get(
          `${getApiUrl()}/api/v2/trend-reports/latest`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        setState({ status: "ok", report: res.data?.report || null });
      } catch (e) {
        if (!cancelled) setState({ status: "error", report: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  const card =
    "bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm";

  const goToAllReports = () => {
    const params = new URLSearchParams(location.search);
    navigate(`/trend-reports?${params.toString()}`);
  };

  const report = state.report;
  const payload = report?.payload || {};
  const stats = payload.stats || {};
  const sections = Array.isArray(payload.sections) ? payload.sections : [];
  const title =
    bi(payload.title, language) || trendT("fallbackTitle", language);

  return (
    <div className="flex w-full h-screen bg-slate-100 dark:bg-dashboard-bg text-black font-sans overflow-hidden">
      <LanguageSwitcher language={language} setLanguage={setLanguage} />

      <div className="relative flex-1 flex flex-col overflow-hidden pt-16">
        {/* Page-level loading veil while the report fetches */}
        {state.status === "loading" && (
          <div className="absolute inset-0 z-40 flex items-center justify-center bg-slate-100/50 dark:bg-dashboard-bg/50 backdrop-blur-[1px] pointer-events-none">
            <div className="flex items-center gap-2 rounded-full bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 px-4 py-2">
              <svg
                className="w-4 h-4 text-orange-500 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                {trendT("loading", language)}
              </span>
            </div>
          </div>
        )}
        <div className="flex-1 overflow-y-auto no-scrollbar">
          <div className="w-full mx-auto px-4 lg:px-8 pt-4 pb-8">
            {/* ── Greeting ─────────────────────────────────────── */}
            <div className="mb-4 flex flex-col items-center gap-1">
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
                {greeting}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Here's what's happening on your dashboard today.
              </p>
            </div>

            {/* ── Daily Trend Report (always expanded — the only content on
                this page, so a collapse toggle would just add a click) ──── */}
            {state.status === "error" && (
              <div className={`${card} p-6`}>
                <p className="text-sm text-red-500">
                  {trendT("error", language)}
                </p>
              </div>
            )}

            {state.status === "ok" && !report && (
              <div className={`${card} p-6`}>
                <p className="text-sm text-gray-400">
                  {trendT("empty", language)}
                </p>
              </div>
            )}

            {report && (
              <ArticlePreviewProvider>
                <div className={`${card} overflow-hidden`}>
                  <div className="flex items-start justify-between gap-4 p-5 border-b border-gray-100 dark:border-gray-700/60">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-500/20 shrink-0">
                        <svg
                          className="w-5 h-5 text-orange-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                          />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-base font-bold text-gray-900 dark:text-white leading-snug">
                          {title}
                        </h2>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                            {formatDate(
                              payload.report_date || report.report_date,
                              language,
                            )}
                          </span>
                          <span
                            className="text-gray-300 dark:text-gray-600"
                            aria-hidden
                          >
                            ·
                          </span>
                          <span className="text-xs text-gray-400">
                            {trendT("subtitle", language)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={goToAllReports}
                      className="text-base font-semibold text-orange-600 dark:text-orange-400 hover:underline whitespace-nowrap shrink-0"
                    >
                      {trendT("viewAllReports", language)} →
                    </button>
                  </div>

                  <div className="p-5 space-y-5">
                    {/* Stat strip */}
                    <div className="flex items-center gap-6">
                      <StatPill
                        value={stats.total_items ?? 0}
                        label={trendT("items", language)}
                      />
                      <StatPill
                        value={stats.applications_with_news ?? stats.topics_with_news ?? 0}
                        label={trendT("applicationsWithNews", language)}
                      />
                    </div>

                    {sections.map((section, i) => (
                      <Section
                        key={i}
                        section={section}
                        lang={language}
                        sources={payload.sources}
                      />
                    ))}

                    <AllNews
                      groups={report.news}
                      lang={language}
                      sources={payload.sources}
                    />
                  </div>
                </div>
              </ArticlePreviewProvider>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TrendDashboardPage;
